/**
 * SessionStorageを使った選択状態の管理
 * ブラウザバック時の選択状態保持に使用
 */

const SESSION_STORAGE_KEY = 'participationSelectionState';

/**
 * 参加画面の選択状態をSessionStorageに保存
 * @param {Object} selectionData 保存する選択状態データ
 */
export const saveParticipationSelection = (selectionData) => {
  try {
    // 既存のデータがあれば取得してマージ
    const existingData = restoreParticipationSelection() || {};

    const dataToSave = {
      ...existingData,
      ...selectionData,
      timestamp: Date.now() // 保存時刻を記録
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to save participation selection to sessionStorage:',
      error
    );
  }
};

/**
 * SessionStorageから参加画面の選択状態を復元
 * @returns {Object|null} 復元された選択状態データまたはnull
 */
export const restoreParticipationSelection = () => {
  try {
    const savedData = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // 1時間以内のデータのみ有効とする（必要に応じて調整）
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      if (parsedData.timestamp && parsedData.timestamp > oneHourAgo) {
        return parsedData;
      } else {
        // 古いデータは削除
        clearParticipationSelection();
        return null;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to restore participation selection from sessionStorage:',
      error
    );
  }
  return null;
};

/**
 * SessionStorageから参加画面の選択状態を削除
 */
export const clearParticipationSelection = () => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      'Failed to clear participation selection from sessionStorage:',
      error
    );
  }
};

/**
 * 選択状態データのフォーマット
 * @param {Object} state 現在のstateオブジェクト
 * @returns {Object} SessionStorage用にフォーマットされたデータ
 */
export const formatSelectionData = (state) => {
  return {
    selectedPrefecture: state.selectedPrefecture,
    participatingTimeSlots: state.participatingTimeSlots || [],
    selectedSlotPrefecture: state.selectedSlotPrefecture || {},
    prefectureAreas: state.prefectureAreas,
    thisWeekSelected: state.thisWeekSelected,
    nextWeekSelected: state.nextWeekSelected,
    nextNextWeekSelected: state.nextNextWeekSelected,
    weeks: state.weeks
    // 週別データは weeks から復元するため、個別に保存しない
  };
};

/**
 * 復元されたデータをstate形式に変換
 * @param {Object} savedData SessionStorageから復元されたデータ
 * @returns {Object} setState用のオブジェクト
 */
export const formatRestoredData = (savedData) => {
  // weeks配列から週別データを正しく復元
  const weeks = savedData.weeks || [[], [], []];

  return {
    selectedPrefecture: savedData.selectedPrefecture,
    participatingTimeSlots: savedData.participatingTimeSlots || [],
    selectedSlotPrefecture: savedData.selectedSlotPrefecture || {},
    prefectureAreas: savedData.prefectureAreas,
    thisWeekSelected: savedData.thisWeekSelected,
    nextWeekSelected: savedData.nextWeekSelected,
    nextNextWeekSelected: savedData.nextNextWeekSelected,
    weeks: weeks,
    thisWeekDates: weeks[0] || [],
    nextWeekDates: weeks[1] || [],
    nextNextWeekDates: weeks[2] || [],
    isCheckDateSelected: (savedData.participatingTimeSlots || []).length > 0
  };
};

/**
 * datingConfirmation以外への遷移時にSessionStorageをクリアする
 * @param {string} targetPath 遷移先のパス
 */
export const clearSelectionOnNonDatingConfirmationNavigation = (targetPath) => {
  // datingConfirmationページ以外への遷移の場合はSessionStorageをクリア
  const shouldKeep = shouldKeepSessionStorage(targetPath);

  if (!shouldKeep) {
    clearParticipationSelection();
  }
};

/**
 * 指定したパスがSessionStorageを保持すべきパスかどうかを判定
 * @param {string} path チェックするパス
 * @returns {boolean} SessionStorageを保持すべきかどうか
 */
export const shouldKeepSessionStorage = (path) => {
  const keepPaths = ['/participation', '/dating-confirmation'];

  return keepPaths.some((keepPath) => path.includes(keepPath));
};
